import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays } from 'date-fns';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { DiscountType, ReleaseType } from '@ibk/shared-types';
import { Calendar, Icon, Input, Toggle, Tooltip } from '@ibk/shared-ui';
import { useBusinessDays } from '../../../../hooks/useBusinessDays';
import { getUnformatedValue, isActiveStatus, shouldUpdateBeneficiaries, updateBeneficiariesDiscounts, updateBeneficiariesValues, validateAndFormatDays, } from '../../../../utils/beneficiaryUtils';
import { applyMask, DEFAULT_CURRENCY_VALUE } from '../../../../utils/maskUtils';
import { isValidNumericValue, MAX_CURRENCY_VALUE } from '../../../../utils/numberUtils';
import { isZeroOrEmptyCurrency, isZeroOrEmptyDays } from '../../../../utils/validationUtils';
import { NewRechargeStep, NewRechargeStepStatus } from '../../types';
import styles from './GlobalSettingsNew.module.css';
import { formatDateForBR, formatDateForISO, getCompetenceString, getDefaultCreditDate, isPayrollDeductionHidden, } from './utils';
const DEFAULT_PERCENTAGE_VALUE = '0,00%';
const MAX_PERCENTAGE_VALUE = 100;
const CALENDAR_MAX_DAYS = 180;
function resolveGlobalValueForReleaseType(product, releaseType, currentGlobalValue, formatCurrency) {
    if (!product) {
        return currentGlobalValue;
    }
    if (releaseType === ReleaseType.MONTHLY) {
        return product.baseValue ? formatCurrency(product.baseValue) : DEFAULT_CURRENCY_VALUE;
    }
    if (releaseType === ReleaseType.DAILY) {
        return product.amountDaily ? formatCurrency(product.amountDaily) : DEFAULT_CURRENCY_VALUE;
    }
    return currentGlobalValue;
}
function buildLocalDate(value) {
    if (!value) {
        return undefined;
    }
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
        return undefined;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
}
function formatCurrencyBR(value) {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function buildUpdatedSelectedList(selectedList, numericValue, releaseType, days) {
    if (!selectedList?.length) {
        return undefined;
    }
    const isDaily = releaseType === ReleaseType.DAILY;
    return selectedList.map(selected => isDaily
        ? { ...selected, value: days > 0 ? numericValue * days : 0, daysQuantity: days }
        : { ...selected, value: numericValue, daysQuantity: 0 });
}
export function GlobalSettingsNew({ formData, isLoading = false, onComplete, onReturn, onSelectCreditDate, onPayrollDeductionChange, onDiscountTypeChange, onDiscountValueChange, onUpdateBeneficiariesData, products = [], isDiscountEnabled = true, headerStepper, }) {
    const { t, currentLanguage } = useTranslation();
    const [showCalendar, setShowCalendar] = useState(false);
    const [valueError, setValueError] = useState(false);
    const [daysError, setDaysError] = useState(false);
    const hasInitializedCreditDate = useRef(false);
    const showPayrollDeduction = isDiscountEnabled && !isPayrollDeductionHidden(formData.voucherType);
    const releaseType = formData.releaseType;
    const isMonthlySelected = !releaseType || releaseType === ReleaseType.MONTHLY;
    const isDailySelected = releaseType === ReleaseType.DAILY;
    const breadcrumbs = useMemo(() => [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ], [t]);
    const currentStepConfig = useMemo(() => ({
        id: NewRechargeStep.GLOBAL_SETTINGS,
        title: t('recharge.step.global-settings.title.release-credit-calculation'),
        number: 3,
        status: NewRechargeStepStatus.ACTIVE,
    }), [t]);
    useEffect(() => {
        if (hasInitializedCreditDate.current) {
            return;
        }
        if (formData.creditDate) {
            hasInitializedCreditDate.current = true;
            return;
        }
        hasInitializedCreditDate.current = true;
        onSelectCreditDate(getDefaultCreditDate(new Date()));
    }, [formData.creditDate, onSelectCreditDate]);
    const competenceString = getCompetenceString(formData.competenceDate);
    const { businessDays: suggestedBusinessDays, isLoading: isLoadingBusinessDays } = useBusinessDays({
        competence: competenceString,
        enabled: !!competenceString && releaseType === ReleaseType.DAILY,
    });
    useEffect(() => {
        if (suggestedBusinessDays !== null &&
            suggestedBusinessDays !== undefined &&
            formData.businessDays === undefined &&
            onUpdateBeneficiariesData &&
            !isLoadingBusinessDays) {
            onUpdateBeneficiariesData({ businessDays: suggestedBusinessDays });
        }
    }, [suggestedBusinessDays, formData.businessDays, onUpdateBeneficiariesData, isLoadingBusinessDays]);
    const handleSelectDate = useCallback((date) => {
        if (typeof date !== 'string') {
            onSelectCreditDate(undefined);
            return;
        }
        onSelectCreditDate(buildLocalDate(date));
    }, [onSelectCreditDate]);
    const handleBack = useCallback(() => {
        onReturn();
    }, [onReturn]);
    const handleNext = useCallback(() => {
        const effectiveReleaseType = releaseType ?? ReleaseType.MONTHLY;
        let hasError = false;
        if (isZeroOrEmptyCurrency(formData.globalValue)) {
            setValueError(true);
            hasError = true;
        }
        if (effectiveReleaseType === ReleaseType.DAILY && isZeroOrEmptyDays(formData.businessDays)) {
            setDaysError(true);
            hasError = true;
        }
        if (hasError) {
            return;
        }
        onComplete(true);
    }, [onComplete, releaseType, formData.globalValue, formData.businessDays]);
    const isValidForm = useMemo(() => {
        return Boolean(formData.creditDate);
    }, [formData.creditDate]);
    const calendarInitialDate = formatDateForISO(formData.creditDate ?? new Date());
    const calendarMinDate = formatDateForISO(new Date());
    const calendarMaxDate = formatDateForISO(addDays(new Date(), CALENDAR_MAX_DAYS));
    const checkShouldUpdateBeneficiaries = useCallback((rt, globalValue) => shouldUpdateBeneficiaries(rt, globalValue, formData.beneficiaries, DEFAULT_CURRENCY_VALUE), [formData.beneficiaries]);
    const handleReleaseTypeChange = useCallback((value) => {
        if (value === releaseType) {
            return;
        }
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const product = products.find(p => p.voucherTypeDescription === formData.voucherType);
        const globalValue = resolveGlobalValueForReleaseType(product, value, formData.globalValue ?? DEFAULT_CURRENCY_VALUE, formatCurrencyBR);
        const shouldSuggestBusinessDays = value === ReleaseType.DAILY && isZeroOrEmptyDays(formData.businessDays);
        const updateData = {
            releaseType: value,
            globalValue,
            amountDaily: value === ReleaseType.DAILY ? getUnformatedValue(globalValue) : undefined,
            ...(shouldSuggestBusinessDays ? { businessDays: undefined } : {}),
        };
        if (checkShouldUpdateBeneficiaries(value, globalValue) && formData.beneficiaries) {
            const numericValue = getUnformatedValue(globalValue);
            const days = value === ReleaseType.DAILY ? (formData.businessDays ?? 0) : 0;
            updateData.beneficiaries = updateBeneficiariesValues(formData.beneficiaries, numericValue, value, days);
            const updatedSelected = buildUpdatedSelectedList(formData.selectedBeneficiariesList, numericValue, value, days);
            if (updatedSelected) {
                updateData.selectedBeneficiariesList = updatedSelected;
            }
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        releaseType,
        onUpdateBeneficiariesData,
        products,
        formData.voucherType,
        formData.globalValue,
        formData.businessDays,
        formData.beneficiaries,
        formData.selectedBeneficiariesList,
        checkShouldUpdateBeneficiaries,
    ]);
    const handleGlobalValueUpdate = useCallback((value) => {
        if (!isZeroOrEmptyCurrency(value)) {
            setValueError(false);
        }
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const updateData = {
            globalValue: value,
            amountDaily: releaseType === ReleaseType.DAILY ? getUnformatedValue(value) : undefined,
        };
        if (checkShouldUpdateBeneficiaries(releaseType, value) && formData.beneficiaries && releaseType) {
            const numericValue = getUnformatedValue(value);
            const days = releaseType === ReleaseType.DAILY ? (formData.businessDays ?? 0) : 0;
            updateData.beneficiaries = updateBeneficiariesValues(formData.beneficiaries, numericValue, releaseType, days);
            const updatedSelected = buildUpdatedSelectedList(formData.selectedBeneficiariesList, numericValue, releaseType, days);
            if (updatedSelected) {
                updateData.selectedBeneficiariesList = updatedSelected;
            }
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        onUpdateBeneficiariesData,
        releaseType,
        formData.beneficiaries,
        formData.businessDays,
        formData.selectedBeneficiariesList,
        checkShouldUpdateBeneficiaries,
    ]);
    const handleMaskedGlobalValueChange = useCallback((value) => {
        const unformatted = getUnformatedValue(value);
        if (!isValidNumericValue(unformatted)) {
            return;
        }
        handleGlobalValueUpdate(applyMask(value, DiscountType.TOTAL));
    }, [handleGlobalValueUpdate]);
    const handleBusinessDaysUpdate = useCallback((numericValue) => {
        if (numericValue > 0) {
            setDaysError(false);
        }
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const updateData = { businessDays: numericValue };
        if (checkShouldUpdateBeneficiaries(releaseType, formData.globalValue) &&
            releaseType === ReleaseType.DAILY &&
            formData.globalValue &&
            formData.beneficiaries &&
            numericValue > 0) {
            const numeric = getUnformatedValue(formData.globalValue);
            updateData.beneficiaries = formData.beneficiaries.map(beneficiary => {
                if (isActiveStatus(beneficiary.statusInProduct)) {
                    return {
                        ...beneficiary,
                        productValue: numeric * numericValue,
                        productDaysQuantity: numericValue,
                    };
                }
                return beneficiary;
            });
            const updatedSelected = buildUpdatedSelectedList(formData.selectedBeneficiariesList, numeric, ReleaseType.DAILY, numericValue);
            if (updatedSelected) {
                updateData.selectedBeneficiariesList = updatedSelected;
            }
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        onUpdateBeneficiariesData,
        releaseType,
        formData.globalValue,
        formData.beneficiaries,
        formData.selectedBeneficiariesList,
        checkShouldUpdateBeneficiaries,
    ]);
    const handleBusinessDaysInputChange = useCallback((value) => {
        const valid = validateAndFormatDays(value);
        const days = valid === '' ? 0 : parseInt(valid, 10) || 0;
        handleBusinessDaysUpdate(days);
    }, [handleBusinessDaysUpdate]);
    const propagateDiscountToBeneficiaries = useCallback((nextDiscountValue, payrollDeductionEnabled) => {
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const update = {};
        if (formData.beneficiaries?.length) {
            update.beneficiaries = updateBeneficiariesDiscounts(formData.beneficiaries, nextDiscountValue, isDiscountEnabled, payrollDeductionEnabled);
        }
        if (formData.selectedBeneficiariesList?.length) {
            const hasDiscount = isDiscountEnabled && payrollDeductionEnabled && !!nextDiscountValue;
            const discountNumericValue = hasDiscount ? getUnformatedValue(nextDiscountValue ?? '') : 0;
            update.selectedBeneficiariesList = formData.selectedBeneficiariesList.map(selected => ({
                ...selected,
                discountValue: discountNumericValue,
            }));
        }
        if (Object.keys(update).length > 0) {
            onUpdateBeneficiariesData(update);
        }
    }, [onUpdateBeneficiariesData, formData.beneficiaries, formData.selectedBeneficiariesList, isDiscountEnabled]);
    const handleTogglePayrollDeduction = useCallback((checked) => {
        onPayrollDeductionChange(checked);
        if (checked) {
            onDiscountTypeChange(DiscountType.TOTAL);
            propagateDiscountToBeneficiaries(formData.discountValue, true);
        }
        else {
            onDiscountTypeChange(DiscountType.NO_DISCOUNT);
            onDiscountValueChange('');
            propagateDiscountToBeneficiaries('', false);
        }
    }, [
        onPayrollDeductionChange,
        onDiscountTypeChange,
        onDiscountValueChange,
        propagateDiscountToBeneficiaries,
        formData.discountValue,
    ]);
    const handleDiscountTypeChange = useCallback((value) => {
        if (value === formData.discountType) {
            return;
        }
        const nextValue = value === DiscountType.PERCENTAGE ? DEFAULT_PERCENTAGE_VALUE : DEFAULT_CURRENCY_VALUE;
        onDiscountTypeChange(value);
        onDiscountValueChange(nextValue);
        propagateDiscountToBeneficiaries(nextValue, formData.payrollDeduction ?? false);
    }, [
        formData.discountType,
        formData.payrollDeduction,
        onDiscountTypeChange,
        onDiscountValueChange,
        propagateDiscountToBeneficiaries,
    ]);
    const handleDiscountValueInputChange = useCallback((value) => {
        const type = formData.discountType ?? DiscountType.TOTAL;
        const isPercentage = type === DiscountType.PERCENTAGE;
        const numbers = value.replace(/\D/g, '');
        const maxValue = isPercentage ? MAX_PERCENTAGE_VALUE : MAX_CURRENCY_VALUE;
        const numericValue = parseFloat(numbers || '0') / 100;
        const masked = numericValue > maxValue
            ? applyMask(isPercentage ? `${maxValue}.00` : `${maxValue}`, type)
            : applyMask(value, type);
        onDiscountValueChange(masked);
        propagateDiscountToBeneficiaries(masked, formData.payrollDeduction ?? false);
    }, [formData.discountType, formData.payrollDeduction, onDiscountValueChange, propagateDiscountToBeneficiaries]);
    const renderCalculationCard = (type, labelKey, identifier) => {
        const selected = type === ReleaseType.MONTHLY ? isMonthlySelected : isDailySelected;
        const cardClassName = `${styles.calculationCard} ${selected ? styles.calculationCardSelected : ''}`.trim();
        const radioClassName = `${styles.calculationRadio} ${selected ? styles.calculationRadioSelected : ''}`.trim();
        return (_jsxs("label", { className: cardClassName, children: [_jsx("input", { type: 'radio', name: 'newRechargeGlobalSettingsCalculation', value: identifier, checked: selected, onChange: () => handleReleaseTypeChange(type), className: styles.visuallyHiddenInput, "data-testid": `radioNewRechargeGlobalSettingsCalculation-${identifier}` }), _jsx("span", { className: radioClassName, "aria-hidden": 'true' }), _jsx("span", { className: styles.calculationLabel, children: t(labelKey) })] }));
    };
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: undefined, noMaxWidth: true, noBackground: true, children: _jsxs(StepperCard, { currentStep: currentStepConfig, showStepper: false, actions: {
                back: {
                    label: t('recharge.button.back'),
                    onClick: handleBack,
                },
                next: {
                    label: t('recharge.button.next'),
                    onClick: handleNext,
                    loading: isLoading,
                    disabled: isLoading || !isValidForm,
                },
            }, children: [headerStepper, _jsxs("div", { className: styles.content, "data-testid": 'viewNewRechargeGlobalSettingsStepContent', children: [_jsx("span", { className: styles.mandatoryLegend, "data-testid": 'textNewRechargeGlobalSettingsMandatoryLegend', children: t('recharge.step.global-settings.mandatoryFields') }), _jsx("p", { className: styles.subtitle, "data-testid": 'textNewRechargeGlobalSettingsSubtitle', children: t('recharge.step.global-settings.subtitle') }), _jsxs("div", { className: styles.fieldGroup, children: [_jsx("div", { className: styles.labelRow, children: _jsxs("span", { className: styles.labelText, children: [t('recharge.step.global-settings.releaseCredit.label'), _jsx("span", { className: styles.requiredAsterisk, "aria-hidden": 'true', children: ' *' })] }) }), _jsxs("div", { className: styles.dateInputRow, children: [_jsx("input", { className: styles.dateInput, type: 'text', onFocus: () => setShowCalendar(true), readOnly: true, value: formatDateForBR(formData.creditDate, currentLanguage), "data-testid": 'inputNewRechargeGlobalSettingsCreditDate', "aria-label": t('recharge.step.global-settings.releaseCredit.label') }), _jsx("button", { type: 'button', className: styles.calendarButton, onClick: () => setShowCalendar(prev => !prev), "data-testid": 'buttonNewRechargeGlobalSettingsCreditDateCalendar', "aria-label": t('recharge.step.global-settings.releaseCredit.label'), children: _jsx(Icon, { name: 'calendar', className: styles.calendarIcon, alt: '' }) })] }), _jsx(Calendar, { dataTestId: 'calendarNewRechargeGlobalSettings', onlyCalendar: true, selectionType: 'single', select: handleSelectDate, open: showCalendar, close: () => setShowCalendar(false), automaticClosable: true, minDate: calendarMinDate, maxDate: calendarMaxDate, initialDate: calendarInitialDate }), _jsx("span", { className: styles.helperText, children: t('recharge.step.global-settings.releaseCredit.helper') })] }), _jsxs("div", { className: styles.fieldGroup, children: [_jsxs("div", { className: styles.labelRow, children: [_jsxs("span", { className: styles.labelText, children: [t('recharge.step.global-settings.calculation.label'), _jsx("span", { className: styles.requiredAsterisk, "aria-hidden": 'true', children: ' *' })] }), _jsx(Tooltip, { content: t('recharge.step.global-settings.calculation.tooltip'), position: 'top', trigger: 'hover', dataTestId: 'tooltipNewRechargeGlobalSettingsCalculation', children: _jsx("span", { className: styles.infoButton, "aria-label": t('recharge.step.global-settings.calculation.label'), children: _jsx(Icon, { name: 'informationGray', className: styles.infoIcon, alt: '' }) }) })] }), _jsxs("div", { className: styles.calculationGrid, children: [renderCalculationCard(ReleaseType.MONTHLY, 'recharge.step.global-settings.calculation.monthly', 'monthly'), renderCalculationCard(ReleaseType.DAILY, 'recharge.step.global-settings.calculation.daily', 'daily')] })] }), _jsxs("div", { className: styles.fieldGroup, children: [_jsx("div", { className: styles.labelRow, children: _jsxs("span", { className: styles.labelText, children: [t('recharge.step.global-settings.value.label'), _jsx("span", { className: styles.requiredAsterisk, "aria-hidden": 'true', children: ' *' })] }) }), _jsx(Input, { dataTestId: 'inputNewRechargeGlobalSettingsValue', size: 'large', placeholder: t('recharge.step.global-settings.value.placeholder'), value: formData.globalValue ?? DEFAULT_CURRENCY_VALUE, onChange: event => handleMaskedGlobalValueChange(event.target.value), error: valueError ? t('recharge.validation.zero-value-field') : undefined })] }), isDailySelected && (_jsxs("div", { className: styles.fieldGroup, children: [_jsx("div", { className: styles.labelRow, children: _jsxs("span", { className: styles.labelText, children: [t('recharge.step.global-settings.businessDays.label'), _jsx("span", { className: styles.requiredAsterisk, "aria-hidden": 'true', children: ' *' })] }) }), _jsx(Input, { dataTestId: 'inputNewRechargeGlobalSettingsBusinessDays', size: 'large', type: 'text', maxLength: 2, placeholder: t('recharge.step.global-settings.businessDays.placeholder'), value: formData.businessDays && formData.businessDays > 0 ? formData.businessDays.toString() : '', onChange: event => handleBusinessDaysInputChange(event.target.value), error: daysError ? t('recharge.validation.zero-value-field') : undefined })] })), showPayrollDeduction && (_jsxs("section", { className: styles.payrollSection, "data-testid": 'sectionNewRechargeGlobalSettingsPayroll', children: [_jsxs("div", { className: styles.payrollHeader, children: [_jsx("h3", { className: styles.payrollTitle, children: t('recharge.step.global-settings.payrollDeduction.label') }), _jsx(Toggle, { checked: formData.payrollDeduction ?? false, onChange: handleTogglePayrollDeduction, ariaLabel: t('recharge.step.global-settings.payrollDeduction.label'), dataTestid: 'toggleNewRechargeGlobalSettingsPayrollDeduction' })] }), formData.payrollDeduction && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.discountRadioGroup, children: [_jsxs("label", { className: styles.discountRadio, children: [_jsx("input", { type: 'radio', name: 'newRechargeGlobalSettingsDiscountType', value: 'amount', checked: formData.discountType === DiscountType.TOTAL, onChange: () => handleDiscountTypeChange(DiscountType.TOTAL), className: styles.visuallyHiddenInput, "data-testid": 'radioNewRechargeGlobalSettingsDiscountAmount' }), _jsx("span", { className: `${styles.calculationRadio} ${formData.discountType === DiscountType.TOTAL ? styles.calculationRadioSelected : ''}`.trim(), "aria-hidden": 'true' }), _jsx("span", { className: styles.discountRadioLabel, children: t('parameters.payroll-deduction.amount') })] }), _jsxs("label", { className: styles.discountRadio, children: [_jsx("input", { type: 'radio', name: 'newRechargeGlobalSettingsDiscountType', value: 'percentage', checked: formData.discountType === DiscountType.PERCENTAGE, onChange: () => handleDiscountTypeChange(DiscountType.PERCENTAGE), className: styles.visuallyHiddenInput, "data-testid": 'radioNewRechargeGlobalSettingsDiscountPercentage' }), _jsx("span", { className: `${styles.calculationRadio} ${formData.discountType === DiscountType.PERCENTAGE ? styles.calculationRadioSelected : ''}`.trim(), "aria-hidden": 'true' }), _jsx("span", { className: styles.discountRadioLabel, children: t('parameters.payroll-deduction.percentage') })] })] }), _jsx("div", { className: styles.discountInputWrapper, children: _jsx(Input, { dataTestId: 'inputNewRechargeGlobalSettingsDiscountValue', size: 'large', type: 'text', inputMode: 'numeric', placeholder: formData.discountType === DiscountType.PERCENTAGE
                                                    ? t('parameters.payroll-deduction.percentage.placeholder')
                                                    : t('parameters.payroll-deduction.amount.placeholder'), value: formData.discountValue ?? '', onChange: event => handleDiscountValueInputChange(event.target.value) }) })] }))] }))] })] }) }));
}
